import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { needsTermsAcceptance } from '@floorpoll/shared';
import { useAuth } from '../lib/auth.js';
import { AcceptTerms } from '../pages/legal/AcceptTerms.js';

/** Gates console routes. Public views (join, big screen) never go through this. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, sessionStatus, user } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    // A reload during an outage lands here rather than at /signin: the refresh
    // token survived, so saying "Loading…" would understate what is happening.
    return (
      <div className="center-page">
        <p className="muted" role="status">
          {sessionStatus === 'reconnecting' ? 'Reconnecting…' : 'Loading…'}
        </p>
      </div>
    );
  }

  if (status === 'signed-out') {
    // Passed as a query param because that is what SignIn reads; router state
    // would be silently dropped and the user would land on the dashboard.
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?next=${encodeURIComponent(next)}`} replace />;
  }

  // Enforced here rather than on the signup form, which a Google sign-up never
  // touches. One branch covers every way an account comes into existence.
  if (needsTermsAcceptance(user)) {
    return <AcceptTerms returning={Boolean(user?.termsVersion)} />;
  }

  return <>{children}</>;
}
